<template>
  <div>
    <DataTable
    :items.sync="mappedItems"
    :headers="headers"
    :loading="loading"
    @create="editTicket"
    defaultSort="ticketNumber"
    :sortDesc="true"
    tableKey="ticketId"
    @refresh="$emit('refresh')"
    @email-csv="emailCSV"
    :actions="tableActions"
    :customCells="customColumns"
    :showSearch="isTractDetail"
    resize
    :tableHeightCalculationData="tableHeightCalculationData"
    :htmlIdentifier="getHtmlIdentifier"
    :tableElementId="isTractDetail ? 'tract-detail-tickets-table' : 'tickets-table'"
    @click:row="onRowClick"
    :serverSide="serverSide"
    :serverItemsLength="serverItemsLength"
    @update:options="o => $emit('update:options', o)"
    showPointerCursor>
      <template #search-replacement v-if="!isTractDetail">
        <v-col style="min-width: 200px;" class="pa-0">
          <v-text-field
            v-model="search"
            :label="$t('search')"
            @keydown.enter="searchBtnClicked"
            @click:clear="searchBtnClicked(true)"
            append-icon="mdi-magnify"
            single-line
            clearable
            color="primary"
          >
            <template #append-outer>
              <v-btn small color="primary" class="mr-6" :disabled="!search" @click="searchBtnClicked">
                Search
              </v-btn>
            </template>
          </v-text-field>
        </v-col>
      </template>
      <template #custom-controls>
        <!-- <template v-if="!isTractDetail">
          <v-col cols="auto">
            <v-row
            :dense="$vuetify.breakpoint.mdAndUp"
            :no-gutters="$vuetify.breakpoint.smAndDown">
              <v-col v-for="filter in filterButtons" :key="filter.name" cols="auto" :class="$vuetify.breakpoint.smAndDown ? 'pr-1' : ''">
                <Icon
                  :iconColor="filter.active ? 'black' : 'grey'"
                  :icon="filter.icon"
                  :tooltipText="$t(filter.name)"
                  :small="false"
                  @icon-clicked="filterClicked(filter)"
                  :elementId="`${kebabCase(filter.name)}-filter-tickets-table`"
                  margin=""
                />
              </v-col>
            </v-row>
          </v-col>
        </template> -->
        <v-col cols="auto">
          <v-row no-gutters class="my-2" align="center">
            <v-col cols="auto" v-if="$vuetify.breakpoint.xl && !isTractDetail">
              <TicketFilterChips/>
            </v-col>
            <v-col cols="auto" v-if="$vuetify.breakpoint.xl || isTractDetail">
              <div style="height: 48px; border-left: solid rgba(0, 0, 0, .12) 1px; margin-left: 17px; margin-right: 18px;"/>
            </v-col>
            <v-col cols="auto">
              <Icon
              :small="false"
              icon="mdi-dots-vertical"
              elementId="ticket-context-menu-activator"
              @icon-clicked="openTicketContextMenu"/>
              <v-menu
              v-model="ticketContextMenuOpen"
              activator="#ticket-context-menu-activator"
              left
              offset-y>
                <v-list>
                  <v-list-item v-if="showEudr" @click="eudrReportClicked" id="eudr-report-button">
                    <v-avatar>
                      <v-icon size="1.35em" color="secondary">{{ euCircle.path }}</v-icon>
                    </v-avatar>
                    <v-list-item-title>
                      <span>{{ $t('eudrReport') }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="showSortInspector" @click="sortInspectorClicked" id="sort-inspector-button">
                    <v-avatar>
                      <v-icon color="primary">mdi-magnify-scan</v-icon>
                    </v-avatar>
                    <v-list-item-title>
                      <span>{{ $t('sortInspector') }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <TicketScaleDataImporter
                    v-if="!isTractDetail"
                    mode="ticket"
                    @refresh="$emit('refresh')"
                  />
                  <v-list-item v-if="showExpandTickets" :disabled="!userAssignedClaim(UserClaims.TicketManager)" @click="expandTicketsClicked" id="expand-tickets-dialog-button">
                    <v-avatar>
                      <v-icon color="primary">mdi-arrow-expand-horizontal</v-icon>
                    </v-avatar>
                    <v-list-item-title>
                      <span>{{ $t('expandTickets') }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="showEmailCsv" @click="emailCSV" id="live-export-email-csv-button">
                    <v-avatar>
                      <v-icon color="primary">mdi-file-delimited-outline</v-icon>
                    </v-avatar>
                    <v-list-item-title>
                      <span>{{ $t('emailCSV') }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openColumnAdjuster" id="tabbed-column-adjuster-button-tickets-table">
                    <v-avatar>
                        <TabbedColumnAdjuster
                        ref="adjuster"
                        desktopIconSize='1.35em'
                        :isOpen="isOpen"
                        :dialogColor="contractModeColor"
                        :propColumnSet.sync="columnSet"
                        :propTabs="columnAdjusterTabs"
                        @new-columns-selected="newColumnsSelected"
                        :dynamicSize="isTractDetail"
                        appendElementId="tickets-table"
                        :showTooltip="false"/>
                    </v-avatar>
                    <v-list-item-title>
                      <span>{{ $t('adjustColumns') }}</span>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
            <v-col cols="auto">
              <Icon
              v-if="!isTractDetail"
              iconColor="black"
              icon="mdi-clipboard-search"
              :small="false"
              :tooltipText="$t('advancedSearch')"
              @icon-clicked="advanceSearchClicked"
              elementId="advanced-search-button-tickets-table"
              :margin="actionMargin"
              />
            </v-col>
            <v-col cols="auto">
              <Icon
              v-if="showConsumption"
              iconColor="primary"
              icon="mdi-saw-blade"
              :small="false"
              :disabled="!userAssignedClaim(UserClaims.InventoryManager)"
              :tooltipText="$t('inventoryOperations')"
              @icon-clicked="$emit('manage-consumption')"
              elementId="inventory-operations-button-tickets-table"
              :margin="actionMargin"
          />
            </v-col>
            <v-col cols="auto">
              <Icon
              v-if="showTicketReconciliation"
              iconColor="primary"
              icon="mdi-clipboard-check-outline"
              :small="false"
              :tooltipText="$t('reconcileTickets')"
              :disabled="!userAssignedClaim(UserClaims.TicketManager)"
              @icon-clicked="$emit('reconcile')"
              elementId="ticket-reconciliation-button-tickets-table"
              :margin="actionMargin"
          />
            </v-col>
            <v-col cols="auto">
              <Icon
              v-if="!isTractDetail"
              iconColor="primary"
              icon="mdi-briefcase-edit"
              :small="false"
              :margin="actionMargin"
              :tooltipText="$t('managePostedLoads')"
              @icon-clicked="$emit('post-loads')"
              elementId="ticket-posting-button-tickets-table"
              />
            </v-col>
          </v-row>

        </v-col>
      </template>
      <template #filters>
        <v-col cols="auto" class="mx-n4 mt-n4" v-if="$vuetify.breakpoint.lgAndDown && !isTractDetail">
          <TicketFilterChips />
        </v-col>
        <TimeFilter v-if="dateFilter.active"/>
      </template>
      <template #product="slotProps">
        {{ slotProps.item.product ? slotProps.item.product : $t('other') }}
      </template>
      <template #status="slotProps">
        <v-row align="center" justify="center" style="flex-wrap: nowrap;">
          <Icon
          dataTestId="ticket-table-status-icon"
          :iconColor="getStatus(slotProps.item).iconColor"
          :icon="getStatus(slotProps.item).icon"
          :tooltipText="getStatus(slotProps.item).tooltipText"
          :small="false"
          />
          <Icon
          v-if="showScalingIcon(slotProps.item)"
          :iconColor="getScalingStatus(slotProps.item).details.color"
          :icon="getScalingStatus(slotProps.item).details.icon"
          :tooltipText="getScalingStatus(slotProps.item).name"
          :small="false"
          />
          <Icon v-if="slotProps.item.rolloutStatus === RolloutStatus.Active.value" :tooltipText="$t('rolloutActive')" icon="rollout" />
          <Icon
            dataTestId="ticket-table-draft-icon"
            iconColor="black"
            icon="mdi-file"
            v-if="slotProps.item.isOnDraftContract"
            :tooltipText="$t('draftTicket')"
          />
          <Icon
            v-if="slotProps.item.hasImages"
            iconColor="black"
            icon="mdi-image-outline"
            :tooltipText="$t('ticketHasImages')"
          />
          <Icon
            v-if="slotProps.item.hasDefectImages"
            iconColor="grey"
            icon="mdi-image-broken-variant"
            :tooltipText="$t('ticketHasDefectImages')"
          />
          <Icon
            v-if="slotProps.item.consumed"
            dataTestId="ticket-table-consumed-icon"
            iconColor="grey"
            icon="mdi-saw-blade"
            :tooltipText="$t('consumed')"
          />
          <Icon
            v-if="showApprovalStatusWarning(slotProps.item)"
            icon="mdi-file-document-check-outline"
            iconColor="error"
            :tooltipText="approvalStatusWarningText(slotProps.item)"
          />
          <Icon
            v-if="slotProps.item.correctionType !== null"
            icon="mdi-auto-fix"
            iconColor="black"
            :tooltipText="`${$t('ticketCorrection')}: ${CorrectionType.fromInt(parseInt(slotProps.item.correctionType))}`"
          />
          <Icon
            v-if="slotProps.item.proximityTriggered"
            dataTestId="ticket-table-prox-icon"
            icon="mdi-alert-circle-outline"
            iconColor="error"
            @icon-clicked="openMap(slotProps.item)"
            :tooltipText="$t('createdAwayFromTract')"></Icon>
          <Icon
            v-if="allLocationsByAccountId[slotProps.item.destinationAccountId]?.collectLogCounts && !slotProps.item.hasLogCounts && contractMode.value !== 1"
            icon="mdi-numeric-off"
            iconColor="error"
            :tooltipText="$t('missingLogCounts')"
          />
          <Icon
            v-if="(inWeightWarning || outWeightWarning) && checkTicketForWarning(slotProps.item)"
            icon="mdi-alert"
            icon-color="warning"
            :tooltipText="getWarningTooltip(slotProps.item)"
          />
          <Icon
            v-if="slotProps.item.isOverweight"
            icon="weight-alert"
            :tooltipText="$t('overweight')"
          />
          <Icon
            v-if="slotProps.item.containsBaseRate"
            :icon="baseRateMop.path"
            iconColor="warning"
            :tooltipText="$t('baseRateUsed')"
            :small="false"
          />
        </v-row>
      </template>
      <template #source="{item}">
        <Icon :icon="TicketOrigin.forInt(item.origin).icon" :small="false" iconColor="black" :tooltipText="TicketOrigin.fromInt(item.origin)"/>
      </template>
      <template #tract="{item}" v-if="!isTractDetail">
        <Icon
        v-if="item.tractIsCertified"
        icon="mdi-certificate-outline"
        iconColor="black"
        :tooltipText="$t('tractIsCertified')"
        />
        <span>{{item.tract ?? $t('notAvailable')}}</span>
      </template>
      <template #deck-name="{item}">
        <span>{{item.deckName ?? $t('notAvailable')}}</span>
      </template>
      <template #account="{item}">
        <AccountName
          :certificationStatus="item.accountCertificationStatus"
          :accountId="item.accountId"
        >
          <span>{{item.account}}</span>
        </AccountName>
      </template>
      <template #landowner="{item}">
        <span v-if=item.tractLandOwnerName>{{item.tractLandOwnerName ?? $t('notAvailable')}}</span>
        <span v-else-if="item.landownerCount > 1" @click="viewTicket(item)" style="cursor: pointer;">
          <v-tooltip bottom color="black">
            <template #activator="{on}">
              <v-icon v-on="on" color="black">
                mdi-dots-horizontal
              </v-icon>
            </template>
            <span class="subtitle-1 white--text">
              {{ $t('multipleLandowners') }}
            </span>
          </v-tooltip>
        </span>
        <span v-else>
          {{ $t('notAvailable') }}
        </span>
      </template>
      <template #logger="{item}">
        <AccountName
          :certificationStatus="item.loggerCertificationStatus"
          :accountId="item.loggerAccountId"
        >
          <span>{{item.logger ?? $t('notAvailable')}}</span>
        </AccountName>
      </template>
      <template #hauler="{item}">
        <span>{{item.hauler ?? $t('notAvailable')}}</span>
      </template>
      <template #supplier="{item}">
        <span>{{item.supplier ?? $t('notAvailable')}}</span>
      </template>
      <template #driver="{item}">
        <span>{{item.driver ?? $t('notAvailable')}}</span>
      </template>
      <template #consulting-forester="{item}">
        <span>{{item.consultingForester ?? $t('notAvailable')}}</span>
      </template>
      <template #from-account="{item}">
        <AccountName
          :certificationStatus="item.fromAccountCertificationStatus"
          :accountId="item.fromAccountId"
        >
          <span>{{item.fromAccount ?? $t('notAvailable')}}</span>
        </AccountName>
      </template>
      <template #pieces="{item}">
        {{item.pieces > 0 ? item.pieces : $t('notAvailable')}}
      </template>
      <template #related="slotProps">
        <td @click.stop.prevent class="text-center cursor-default">
          <Icon
          elementId="ticket-detail"
          icon="mdi-information-outline"
          margin="mr-1"
          :tooltipText="$t('ticketDetails')"
          @icon-clicked="viewTicket(slotProps.item)"/>
          <Icon
          v-if="!isTractDetail && isLogs"
          elementId="ticket-tract"
          icon="mdi-crosshairs-gps"
          iconColor="black"
          margin="mr-1"
          :tooltipText="$t('viewTract')"
          @icon-clicked="viewTract(slotProps.item)"/>
          <Icon
          elementId="ticket-contract"
          icon="mdi-altimeter"
          :tooltipText="$t('viewContract')"
          small
          @icon-clicked="viewContractDetail(slotProps.item)"
          />
        </td>
      </template>
      <template #actions="slotProps">
        <td @click.stop.prevent class="text-center cursor-default">
          <Icon
          elementId="ticket-edit"
          icon="mdi-pencil"
          iconColor="success"
          :disabled="slotProps.item.status === 6 || slotProps.item.status === 7 || !userAssignedClaim(UserClaims.TicketManager)"
          :tooltipText="$t('edit')"
          @icon-clicked="editTicket(slotProps.item)"/>
          <Icon
          elementId="ticket-delete"
          icon="mdi-delete-forever"
          :disabled="slotProps.item.status === 6 || slotProps.item.status === 7 || !userAssignedClaim(UserClaims.TicketManager)"
          iconColor="error"
          :tooltipText="$t('delete')"
          @icon-clicked="deleteTicket(slotProps.item)"/>
        </td>
      </template>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import {
  TicketStatusIcon,
  ContractApprovalStatus,
  CorrectionType,
  ContractMode,
  UserClaims,
  TicketOrigin,
  RolloutStatus,
  ScalingStatus,
  TicketStatus
} from '@/utils/Enumerations.js'
import TicketHeaders from '@/headers/Ticket'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { kebabCase } from 'lodash'
import { euCircle, baseRateMop } from '@/images/AngelSvgLibrary'

export default {
  name: 'TicketTable',

  components: {
    TicketFilterChips: () => import('@/components/ticket/TicketFilterChips.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    TabbedColumnAdjuster: () => import('@/components/helper/TabbedColumnAdjuster.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    AccountName: () => import('@/components/account/AccountName.vue'),
    TimeFilter: () => import('@/components/ticket/TimeFilter.vue'),
    TicketScaleDataImporter: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataImporter.vue')
  },

  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    contractMode: {
      type: Object,
      default: () => ({ value: 0 })
    },
    isTractDetail: {
      required: false,
      default: false
    },
    serverSide: {
      type: Boolean,
      default: false
    },
    serverItemsLength: {
      type: Number,
      default: undefined
    }
  },

  data: () => ({
    euCircle,
    baseRateMop,
    ScalingStatus,
    RolloutStatus,
    TicketOrigin,
    CorrectionType,
    search: '',
    columnSet: null,
    dateFilter: {
      active: false,
      startDate: '',
      endDate: ''
    },
    ticketMedians: {
      inboundWeight: 0,
      outboundWeight: 0
    },
    inWeightWarning: false,
    outWeightWarning: false,
    ticketMap: false,
    UserClaims,
    ticketContextMenuOpen: false,
    isOpen: false
  }),

  watch: {
    filters: {
      handler (val, _) {
        const dateFilter = val.dateRange
        if (!dateFilter.active) {
          this.dateFilter = {
            active: false,
            startDate: '',
            endDate: ''
          }
        }
      },
      deep: true
    },

    allTickets: {
      handler (val) {
        this.calculateTicketWeightMedians()
        let eligibleInWeights = 0
        let eligibleOutWeights = 0
        this.allTickets.forEach(t => {
          if (t.inWeight !== 0) eligibleInWeights++
          if (t.outWeight !== 0) eligibleOutWeights++
        })
        this.inWeightWarning = eligibleInWeights >= 5
        this.outWeightWarning = eligibleOutWeights >= 5
      }
    },

    contractMode (_) {
      this.initializeHeaders()
    },

    isPhraseFilterActive (val) {
      if (!val) {
        this.search = ''
      }
    }
  },

  computed: {
    ...mapGetters('ticket', ['allTickets', 'ticketsLoading', 'filters']),
    ...mapGetters('tract', ['allTracts']),
    ...mapGetters('locations', ['allLocationsByAccountId']),
    headers () {
      if (!this.columnSet) return []
      return this.columnSet.getColumns()
    },

    mappedItems () {
      return this.allTickets.map(t => Object.assign(t, { landownerBundle: JSON.stringify({ loAccount: t.tractLandOwnerName, loCount: t.landownerCount }) }))
    },

    filterButtons () {
      const filterButtons = Object
        .values(this.filters)
        .filter(i => i.isButton)
      return filterButtons
    },

    scalingFilterButtons () {
      return ScalingStatus.enums.map(ss => ({ name: ss.name, icon: ss.details.icon }))
    },

    isPhraseFilterActive () {
      return this.filters.phrase.active
    },

    columnAdjusterTabs () {
      return ['load', 'misc', 'contract', 'weight', 'volume']
    },

    getHtmlIdentifier () {
      return this.isTractDetail ? 'tract-tickets' : 'ticket-grid'
    },

    customColumns () {
      return [
        {
          slotName: 'source',
          value: 'origin'
        },
        {
          slotName: 'tract',
          value: 'tract'
        },
        {
          slotName: 'account',
          value: 'account'
        },
        {
          slotName: 'landowner',
          value: 'landownerBundle'
        },
        {
          slotName: 'logger',
          value: 'logger'
        },
        {
          slotName: 'hauler',
          value: 'hauler'
        },
        {
          slotName: 'supplier',
          value: 'supplier'
        },
        {
          slotName: 'driver',
          value: 'driver'
        },
        {
          slotName: 'from-account',
          value: 'fromAccount'
        },
        {
          slotName: 'deck-name',
          value: 'deckName'
        },
        {
          slotName: 'related',
          value: 'related'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'status',
          value: 'status'
        },
        {
          slotName: 'product',
          value: 'product'
        },
        {
          slotName: 'pieces',
          value: 'pieces'
        },
        {
          slotName: 'consulting-forester',
          value: 'consultingForester'
        },
        {
          slotName: 'scaling-status',
          value: 'scalingStatus'
        }
      ]
    },

    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh'),
          id: 'refresh-button-tickets-table'
        },
        (!this.isTractDetail) ? {
          actionName: 'create',
          icon: 'mdi-plus',
          text: this.$t('new'),
          disabled: !userAssignedClaim(UserClaims.TicketManager),
          id: 'create-button-tickets-table'
        } : undefined
      ].filter(a => a !== undefined)
    },

    contractModeColor () {
      return ContractMode.forInt(this.contractMode.value).details.color
    },

    isLogs () {
      return this.contractMode === ContractMode.Logs
    },

    isByproducts () {
      return this.contractMode === ContractMode.Byproducts
    },

    isLogYardSales () {
      return this.contractMode === ContractMode.LogYardSale
    },

    isTransfer () {
      return this.contractMode === ContractMode.Transfers
    },

    showTicketReconciliation () {
      return (this.isLogs || this.isByproducts || this.isLogYardSales) && !this.isTractDetail
    },

    showConsumption () {
      return (this.isLogs || this.isByproducts) && !this.isTractDetail
    },

    showEudr () {
      return !this.isTractDetail && this.isLogs
    },

    showSortInspector () {
      return !this.isTractDetail && this.isLogs
    },

    showExpandTickets () {
      return !this.isTractDetail && (this.isLogs || this.isLogYardSales || this.isTransfer)
    },

    showEmailCsv () {
      return !this.isTractDetail
    },

    tableHeightCalculationData () {
      return {
        offset:
        this.isTractDetail
          ? 274
          : this.$vuetify.breakpoint.xl ? 350
            : 397,
        tableElementId: this.isTractDetail ? 'tract-detail-tickets-table' : 'tickets-table'
      }
    },

    actionMargin () {
      return this.$vuetify.breakpoint.smAndDown ? 'mr-1' : 'mr-2'
    }
  },

  created () {
    this.initializeHeaders()
    this.calculateTicketWeightMedians()
  },

  methods: {
    kebabCase,

    ...mapMutations('global', ['setRouterJump']),
    ...mapMutations('ticket', ['applyFilter', 'removeFilter', 'setDateFilter']),
    ...mapMutations('snackbar', ['setSnack']),
    userAssignedClaim,
    openTicketContextMenu () {
      if (document.getElementById('ticket-context-menu-activator')) {
        this.ticketContextMenuOpen = true
      }
    },

    initializeHeaders () {
      let columnSet
      switch (this.contractMode.value) {
        case 0:
          columnSet = TicketHeaders.ticketCRUDHeaders()
          break
        case 1:
          columnSet = TicketHeaders.bpTicketCRUDHeaders()
          break
        case 2:
          columnSet = TicketHeaders.transferColumns()
          break
        case 3:
          columnSet = TicketHeaders.logYardSaleColumns()
          break
      }
      columnSet.columns.forEach(column => column.setCellClass(`ticket-table-${kebabCase(column.value)} ticket-table-cell`))
      this.columnSet = columnSet
      if (this.isTractDetail) {
        this.columnSet.columns = this.columnSet.columns.filter(c => !['tract', 'setting'].includes(c.value))
      }
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    getStatus (ticket) {
      return TicketStatusIcon(ticket?.status, ticket?.transitStatus, ticket?.vehicleType, ticket?.scalingStatus)
    },

    getScalingStatus (ticket) {
      return ScalingStatus.forInt(ticket.scalingStatus)
    },

    calculateTicketWeightMedians () {
      let tickets = [...this.allTickets]

      tickets.sort((a, b) => a.outWeight - b.outWeight)
      tickets = tickets.filter(t => t.outWeight !== 0)
      let midpoint = Math.floor(tickets.length / 2)
      const outboundMedian = tickets.length % 2 === 1
        ? tickets[midpoint]?.outWeight
        : (tickets[midpoint - 1]?.outWeight + tickets[midpoint]?.outWeight) / 2

      tickets = [...this.allTickets]
      tickets.sort((a, b) => a.inWeight - b.inWeight)
      tickets = tickets.filter(t => t.inWeight !== 0)
      midpoint = Math.floor(tickets.length / 2)
      const inboundMedian = tickets.length % 2 === 1
        ? tickets[midpoint]?.inWeight
        : (tickets[midpoint - 1]?.inWeight + tickets[midpoint]?.inWeight) / 2

      this.ticketMedians.outboundWeight = outboundMedian
      this.ticketMedians.inboundWeight = inboundMedian
    },

    checkTicketForWarning (ticket) {
      const inWeightDeltaPercentage = ticket.inWeight > 0 && this.ticketMedians.inboundWeight !== 0 && this.inWeightWarning
        ? Math.abs(ticket.inWeight - this.ticketMedians.inboundWeight) / this.ticketMedians.inboundWeight
        : 0

      const outWeightDeltaPercentage = ticket.outWeight > 0 && this.ticketMedians.outboundWeight !== 0 && this.outWeightWarning
        ? Math.abs(ticket.outWeight - this.ticketMedians.outboundWeight) / this.ticketMedians.outboundWeight
        : 0

      return inWeightDeltaPercentage >= 0.25 || outWeightDeltaPercentage > 0.25
    },

    getWarningTooltip (ticket) {
      let tooltip = ''
      const inWeightDeltaPercentage = ticket.inWeight > 0 && this.ticketMedians.inboundWeight !== 0 && this.inWeightWarning
        ? Math.abs(ticket.inWeight - this.ticketMedians.inboundWeight) / this.ticketMedians.inboundWeight
        : 0

      const outWeightDeltaPercentage = ticket.outWeight > 0 && this.ticketMedians.outboundWeight !== 0 && this.outWeightWarning
        ? Math.abs(ticket.outWeight - this.ticketMedians.outboundWeight) / this.ticketMedians.outboundWeight
        : 0

      if (inWeightDeltaPercentage >= 0.25) tooltip += 'inbound'
      if (outWeightDeltaPercentage >= 0.25) tooltip === '' ? tooltip += 'outbound' : tooltip += ', outbound'

      return this.$t('ticketWeightError', { typeString: tooltip })
    },

    searchBtnClicked (clear) {
      if (clear === true) {
        this.search = ''
      }

      this.$emit('search-btn-clicked', this.search)
    },

    viewTicket (ticket) {
      this.$emit('ticket-action', {
        actionType: 'view-detail',
        ticket: ticket
      })
    },

    viewAccount (ticket) {
      this.$emit('ticket-action', {
        actionType: 'view-account',
        ticket: ticket
      })
    },

    viewContractDetail (ticket) {
      this.$emit('ticket-action', {
        actionType: 'contract-detail',
        ticket: ticket
      })
    },

    viewTract (ticket) {
      this.$emit('ticket-action', {
        actionType: 'view-tract',
        ticket: ticket
      })
    },

    openMap (ticket) {
      this.$emit('ticket-action', {
        actionType: 'open-map',
        ticket: ticket
      })
    },

    editTicket (ticket) {
      this.$emit('ticket-action', {
        actionType: 'open-form',
        ticket: ticket
      })
    },

    deleteTicket (ticket) {
      this.$emit('ticket-action', {
        actionType: 'delete-ticket',
        ticket: ticket
      })
    },

    findTicket () {
      this.$emit('ticket-action', {
        actionType: 'find-ticket'
      })
    },

    advanceSearchClicked () {
      this.$emit('ticket-action', {
        actionType: 'advanced-search'
      })
    },

    emailCSV () {
      this.$emit('ticket-action', {
        actionType: 'email-csv'
      })
    },

    expandTicketsClicked () {
      this.ticketContextMenuOpen = false
      this.$emit('ticket-action', {
        actionType: 'expand-tickets'
      })
    },

    showApprovalStatusWarning (ticket) {
      return ticket.contractApprovalStatus !== ContractApprovalStatus.Approved.value && ticket.contractApprovalStatus !== null
    },

    showScalingIcon (ticket) {
      return ticket.markedForScaling && ticket.scalingStatus && (ticket.status === TicketStatus.WeighedIn.value || ticket.status === TicketStatus.WeighedOut.value)
    },

    approvalStatusWarningText (ticket) {
      return ContractApprovalStatus.fromInt(ticket.contractApprovalStatus)
    },

    importDataClicked () {
      this.$emit('ticket-action', {
        actionType: 'import-scale-sheet'
      })
    },

    eudrReportClicked () {
      this.$emit('ticket-action', {
        actionType: 'eudr-report'
      })
    },

    sortInspectorClicked () {
      this.ticketContextMenuOpen = false
      this.$emit('ticket-action', {
        actionType: 'sort-inspector'
      })
    },

    openColumnAdjuster () {
      this.ticketContextMenuOpen = false
      this.isOpen = !this.isOpen
      this.$refs.adjuster.openMenu()
    },

    onRowClick ({ data }) {
      this.viewTicket(data)
    }
  }
}
</script>

<style>
  #pointer {
    cursor: pointer;
  }
</style>
